/**
 * Two ways in, one contract: msal-react against Entra, or AUTH_MODE=dev where the API acts as
 * a seeded user. Both end up feeding a bearer token (Entra) or a cookie session (dev) to
 * api/client.ts.
 *
 * Boot order matters here. msal-react's MsalProvider seeds `accounts` synchronously from the
 * session cache while `handleRedirectPromise()` is still running, so a returning user renders
 * with an account but no token. React also runs child effects before parent effects, so a token
 * provider installed in an effect is installed AFTER the shell's queries have already fired.
 * Both are handled below: the provider is installed at module scope, and nothing is "signed in"
 * until a token has actually been acquired.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PublicClientApplication, type AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, setAuthMode, setDevActor, setTokenProvider } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { API_SCOPE, DEV_USER_STORAGE_KEY, authMode, loginRequest, msalConfig, type AuthMode } from './config';
import { resolveEntraAuth } from './state';

interface AuthContextValue {
  mode: AuthMode;
  /** MSAL has finished deciding who (if anyone) is signed in. */
  ready: boolean;
  /** A usable credential exists: a bearer token (Entra) or a chosen seeded user (dev). */
  signedIn: boolean;
  /** Whether the last request could carry a credential — a 401 without one is not a sign-out. */
  credentialAttached: boolean;
  /** Entra display name, or the seeded dev user's id. */
  actorLabel: string | null;
  signIn: () => void;
  signOut: () => void;
  /** dev mode only */
  setDevUser: (key: string | null) => void;
}

const AuthContext = createContext<AuthContextValue>({
  mode: 'dev',
  ready: false,
  signedIn: false,
  credentialAttached: false,
  actorLabel: null,
  signIn: () => {},
  signOut: () => {},
  setDevUser: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

function readStoredDevUser(): string | null {
  try {
    return localStorage.getItem(DEV_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

// ──────────────────────────────────────────────────────────────────── dev mode

function DevAuth({ children }: { children: ReactNode }) {
  const [devUser, setDevUserState] = useState<string | null>(() => readStoredDevUser());

  useEffect(() => {
    setTokenProvider(() => null);
    setDevActor(devUser);
  }, [devUser]);

  const setDevUser = useCallback((key: string | null) => {
    try {
      if (key) localStorage.setItem(DEV_USER_STORAGE_KEY, key);
      else localStorage.removeItem(DEV_USER_STORAGE_KEY);
    } catch {
      /* storage blocked — the choice lives for this page load only */
    }
    setDevActor(key);
    setDevUserState(key);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      mode: 'dev',
      ready: true,
      signedIn: Boolean(devUser),
      credentialAttached: Boolean(devUser),
      actorLabel: devUser,
      signIn: () => {},
      signOut: () => {
        void apiFetch(endpoints.logout, { method: 'POST' }).catch(() => undefined);
        setDevUser(null);
      },
      setDevUser,
    }),
    [devUser, setDevUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ────────────────────────────────────────────────────────────────── Entra mode

const msalInstance = authMode === 'entra' ? new PublicClientApplication(msalConfig) : null;

/**
 * Installed at MODULE scope, before React renders anything, so no component can issue a request
 * before the provider exists. api/client.ts refuses to send an Entra request that has no token,
 * so a call made during boot fails locally and retries rather than becoming a cached 401.
 */
if (msalInstance) {
  setAuthMode('entra');
  setDevActor(null);
  setTokenProvider(async () => {
    const active = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!active) return null;
    try {
      const result = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: active });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) await msalInstance.acquireTokenRedirect(loginRequest);
      return null;
    }
  });
}

function EntraAuth({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const queryClient = useQueryClient();
  const account: AccountInfo | undefined = accounts[0];
  const [tokenReady, setTokenReady] = useState(false);

  // One acquisition, once MSAL has settled: only when it resolves is the app "signed in".
  useEffect(() => {
    if (inProgress !== 'none') return;
    const active = instance.getActiveAccount() ?? account;
    if (!active) {
      setTokenReady(false);
      return;
    }
    if (!instance.getActiveAccount()) instance.setActiveAccount(active);

    let cancelled = false;
    void instance
      .acquireTokenSilent({ scopes: [API_SCOPE], account: active })
      .then(() => {
        if (cancelled) return;
        setTokenReady(true);
        // Anything that failed while the token was still being acquired refetches now.
        void queryClient.invalidateQueries();
      })
      .catch(async (err: unknown) => {
        if (cancelled) return;
        setTokenReady(false);
        if (err instanceof InteractionRequiredAuthError) await instance.acquireTokenRedirect(loginRequest);
      });

    return () => {
      cancelled = true;
    };
  }, [account, inProgress, instance, queryClient]);

  const { ready, signedIn } = resolveEntraAuth({
    hasAccount: Boolean(account),
    inProgress,
    tokenReady,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      mode: 'entra',
      ready,
      signedIn,
      credentialAttached: tokenReady,
      actorLabel: account?.name ?? account?.username ?? null,
      signIn: () => void instance.loginRedirect(loginRequest),
      signOut: () => void instance.logoutRedirect(),
      setDevUser: () => {},
    }),
    [account, instance, ready, signedIn, tokenReady],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (authMode === 'dev' || !msalInstance) return <DevAuth>{children}</DevAuth>;
  // MsalProvider runs initialize() + handleRedirectPromise() itself and reports progress through
  // `inProgress`, so there is nothing to await here.
  return (
    <MsalProvider instance={msalInstance}>
      <EntraAuth>{children}</EntraAuth>
    </MsalProvider>
  );
}
